// File Name: ServicesPage.jsx

import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  Cpu,
  Users,
  LineChart,
  HeartHandshake,
  ArrowUpRight,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

/* IMPORT IMAGES */

import itservice from "@/assets/itservice.jpg";
import staffing from "@/assets/staffing.jpg";
import businessconsulting from "@/assets/businessconsulting.jpg";
import humanresources from "@/assets/Humanresources.jpg";

/* SERVICES DATA */

const SERVICES = [
  {
    slug: "it-services",
    title: "IT Services",
    short: "Modern engineering for digital products and platforms.",
    icon: "Cpu",

    image: itservice,

    items: [
      "Web Development",
      "Cloud Solutions",
      "Software Development",
      "Cybersecurity",
    ],
  },

  {
    slug: "staffing-solutions",
    title: "Staffing Solutions",
    short: "Right talent, fast — for permanent and remote roles.",
    icon: "Users",

    image: staffing,

    items: [
      "IT Recruitment",
      "Contract Hiring",
      "Remote Staffing",
      "Talent Acquisition",
    ],
  },

  {
    slug: "business-consulting",
    title: "Business Consulting",
    short: "Strategy and execution that compound across quarters.",
    icon: "LineChart",

    image: businessconsulting,

    items: [
      "Digital Transformation",
      "Technology Consulting",
      "Process Optimization",
      "Business Strategy",
    ],
  },

  {
    slug: "human-resources",
    title: "Human Resources",
    short: "People operations infrastructure for growing teams.",
    icon: "HeartHandshake",

    image: humanresources,

    items: [
      "HR Management",
      "Payroll Support",
      "Employee Solutions",
      "Organizational Development",
    ],
  },
];

/* ICONS */

const ICONS = {
  Cpu,
  Users,
  LineChart,
  HeartHandshake,
};

export default function ServicesPage() {
  return (
    <>
      {/* STYLES */}

      <style>
        {`
          .services-bg {
            background:
              radial-gradient(circle at top right, rgba(139,92,246,0.12), transparent 30%),
              radial-gradient(circle at bottom left, rgba(168,85,247,0.10), transparent 35%);
          }

          .service-image-overlay {
            background:
              linear-gradient(
                to top,
                rgba(0,0,0,0.78),
                rgba(0,0,0,0.22),
                transparent
              );
          }

          .feature-item {
            transition: all 0.3s ease;
          }

          .feature-item:hover {
            transform: translateX(6px);
          }

          .service-image:hover img {
            transform: scale(1.05);
          }

          .service-card {
            transition: all 0.35s ease;
          }

          .service-card:hover {
            transform: translateY(-8px);
          }

          .purple-btn {
            background:
              linear-gradient(
                135deg,
                #7C3AED,
                #A855F7
              );

            transition: all 0.3s ease;
          }

          .purple-btn:hover {
            transform: translateY(-3px);

            box-shadow:
              0 18px 40px rgba(124,58,237,0.28);
          }

          .purple-tag {
            background: #F5F3FF;
            color: #7C3AED;
            border: 1px solid #DDD6FE;
          }

          .purple-icon {
            background: #F5F3FF;
            border: 1px solid #DDD6FE;
          }

          .purple-glow {
            background: rgba(139,92,246,0.16);
            filter: blur(90px);
          }
        `}
      </style>

      {/* HERO SECTION */}

      <section
        className="
          services-bg
          relative
          overflow-hidden
          pt-28
          md:pt-32
          lg:pt-36
          pb-16
          md:pb-24
        "
        data-testid="services-hero"
      >
        <div className="absolute inset-0 grid-bg opacity-40" />

        <div className="absolute top-0 right-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] purple-glow rounded-full" />

        <div className="absolute bottom-0 left-0 w-[250px] md:w-[400px] h-[250px] md:h-[400px] purple-glow rounded-full" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8">

          {/* LABEL */}

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 border border-purple-200 text-purple-700 text-xs sm:text-sm font-semibold mb-6">

            <Sparkles className="w-4 h-4" />

            Enterprise Services

          </div>

          {/* TITLE */}

          <h1
            className="
              text-4xl
              sm:text-5xl
              md:text-7xl
              lg:text-[110px]
              font-bold
              tracking-[-0.05em]
              leading-[1]
              text-[#111827]
              max-w-6xl
            "
            style={{ fontFamily: "Sora" }}
          >
            Everything your business
            <br />

            needs to{" "}

            <span className="bg-gradient-to-r from-purple-600 to-fuchsia-500 bg-clip-text text-transparent">
              scale with confidence.
            </span>

          </h1>

          {/* DESCRIPTION */}

          <p className="mt-6 md:mt-8 text-base md:text-lg text-slate-600 max-w-3xl leading-relaxed">
            We deliver scalable digital solutions, business consulting and
            enterprise technology services that help organizations modernize,
            innovate and grow faster.
          </p>

        </div>
      </section>

      {/* SERVICES SECTION */}

      <section
        className="pb-20 md:pb-28"
        data-testid="services-list"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 space-y-20 md:space-y-28">

          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Cpu;

            const reverse = i % 2 === 1;

            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className={`
                  grid
                  grid-cols-1
                  lg:grid-cols-2
                  gap-10
                  lg:gap-14
                  items-center
                  ${
                    reverse
                      ? "lg:[&>*:first-child]:order-2"
                      : ""
                  }
                `}
              >

                {/* IMAGE SIDE */}

                <div className="service-card service-image relative rounded-[28px] md:rounded-[36px] overflow-hidden h-[340px] sm:h-[420px] md:h-[520px] shadow-[0_20px_60px_rgba(15,23,42,0.10)]">

                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition duration-700"
                    loading="lazy"
                  />

                  {/* OVERLAY */}

                  <div className="absolute inset-0 service-image-overlay" />

                  {/* CONTENT OVER IMAGE */}

                  <div className="absolute bottom-0 left-0 p-6 md:p-10">

                    <div className="text-white/80 text-xs sm:text-sm uppercase tracking-[0.25em] mb-3">
                      {s.title}
                    </div>

                    <h3
                      className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 leading-tight"
                      style={{ fontFamily: "Sora" }}
                    >
                      Modern Enterprise
                      <br />
                      Solutions
                    </h3>

                    <p className="text-sm md:text-base text-white/80 leading-relaxed max-w-md">
                      Delivering innovative business solutions that improve
                      performance, efficiency and long-term scalability.
                    </p>

                  </div>

                </div>

                {/* CONTENT SIDE */}

                <div>

                  {/* ICON */}

                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl purple-icon flex items-center justify-center mb-6 md:mb-7">

                    <Icon className="w-7 h-7 md:w-8 md:h-8 text-purple-600" />

                  </div>

                  {/* TITLE */}

                  <h2
                    className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#111827] mb-5 md:mb-6"
                    style={{ fontFamily: "Sora" }}
                  >
                    {s.title}
                  </h2>

                  {/* SHORT DESCRIPTION */}

                  <p className="text-slate-600 leading-relaxed text-base md:text-lg mb-5 md:mb-6">
                    {s.short}
                  </p>

                  {/* MAIN DESCRIPTION */}

                  <p className="text-sm md:text-base text-slate-500 leading-relaxed mb-8 md:mb-10">
                    Our expert teams help businesses transform operations
                    through innovative technology, scalable infrastructure and
                    strategic consulting designed for sustainable growth.
                  </p>

                  {/* FEATURES */}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8 md:mb-10">

                    {s.items.slice(0, 4).map((it) => (
                      <div
                        key={it}
                        className="feature-item flex items-start gap-3"
                      >

                        <div className="mt-1">

                          <CheckCircle2 className="w-5 h-5 text-purple-500" />

                        </div>

                        <div>

                          <div className="text-[#111827] font-medium text-sm md:text-base">
                            {it}
                          </div>

                          <div className="text-xs md:text-sm text-slate-500 mt-1">
                            Professional enterprise-grade implementation.
                          </div>

                        </div>

                      </div>
                    ))}

                  </div>

                  {/* TAGS */}

                  <div className="flex flex-wrap gap-3 mb-8 md:mb-10">

                    {[
                      "Enterprise Ready",
                      "Cloud Enabled",
                      "24/7 Support",
                    ].map((tag) => (
                      <div
                        key={tag}
                        className="px-4 py-2 rounded-full purple-tag text-xs md:text-sm font-medium"
                      >
                        {tag}
                      </div>
                    ))}

                  </div>

                  {/* BUTTON */}

                  <Link
                    to={`/services/${s.slug}`}
                    className="purple-btn inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-4 rounded-2xl text-white font-semibold shadow-[0_15px_35px_rgba(124,58,237,0.24)]"
                  >
                    Service Details

                    <ArrowUpRight className="w-4 h-4" />

                  </Link>

                </div>

              </motion.div>
            );
          })}

        </div>
      </section>
    </>
  );
}